import React, { KeyboardEvent, useMemo } from "react";
import {
  darkTheme,
  lightTheme,
  spacing,
  typographyTokens
} from "@yleisradio/yds-core";
import styled from "styled-components";

import { ONGOING_STATES } from "../../constants";
import { useEmbedParametersContext } from "../../contexts/embedParametersContext";
import { State } from "../../enums";
import { dateTimeString } from "../../helpers/preview-text";
import { localizedText } from "../../helpers/previewHelpers";
import {
  secondsToHHMMSSFormat,
  secondsToTimeUnits
} from "../../helpers/timeHelpers";
import { useThemeColors } from "../../hooks/useThemeColors/useThemeColors";
import PlayIcon from "../../icons/PlayIcon";
import i18n from "../../service/i18n";
import { usePlayerStore } from "../../store/usePlayerStore";
import { fontFamily } from "../../styles/global";
import { LocalizedText } from "../../types/player-api";
import { NotAllowedButtonAudio } from "../NotAllowedButton/NotAllowedButtonAudio";

import { AudioStrippedButton, IconCircleWrapper } from "./AudioPlayerButtons";
import { ThemeColors } from "./getThemeColors";

const PreviewButtonLabel = styled.div<{ color: string }>`
  color: ${({ color }) => color};
  font-size: ${typographyTokens.FONT_SIZE_XS};
  font-weight: ${typographyTokens.FONT_WEIGHT_NORMAL};

  > span {
    margin-left: ${spacing.SPACING_8};
  }

  ${fontFamily}
`;

const getAriaDuration = (
  durationInSeconds: number | undefined,
  language: string
) => {
  if (durationInSeconds !== undefined) {
    const timeUnits = secondsToTimeUnits(durationInSeconds);
    const hours =
      timeUnits.hours > 0
        ? ` ${timeUnits.hours} ${i18n("tuntia", { language })}`
        : "";
    const minutes =
      timeUnits.minutes > 0
        ? ` ${timeUnits.minutes} ${i18n("minuuttia", { language })}`
        : "";
    const seconds =
      timeUnits.seconds > 0
        ? ` ${timeUnits.seconds} ${i18n("sekuntia", { language })}`
        : "";

    return `${hours}${minutes}${seconds}`;
  } else {
    return "";
  }
};

const getLabels = ({
  audioTitle,
  durationInSeconds,
  language,
  startTime,
  state
}: {
  audioTitle?: LocalizedText;
  durationInSeconds?: number | undefined;
  language: string;
  startTime?: string;
  state: State;
}): { ariaLabel?: string; label: string } => {
  const title = localizedText(audioTitle, language);
  const duration = getAriaDuration(durationInSeconds, language);
  const startTimeStr = dateTimeString(startTime, language, false);

  switch (state) {
    case State.BLOCKED:
    case State.IDENTIFICATION:
      return {
        ariaLabel: `${title} ${i18n("ohjelma_ei_ole_kuunneltavissa_ulkomailla", { language })}`,
        label: i18n("ohjelma_ei_ole_kuunneltavissa_ulkomailla", { language })
      };
    case State.ERROR:
      return {
        label: i18n("toistettavaa_mediaa_ei_loytynyt", { language })
      };
    case State.GONE:
      return {
        ariaLabel: `${title} ${i18n("ohjelma_ei_ole_enaa_kuunneltavissa", { language })}`,
        label: i18n("ohjelma_ei_ole_enaa_kuunneltavissa", { language })
      };
    case State.PENDING_EVENT:
    case State.PENDING_ONDEMAND:
      return {
        ariaLabel: `${title} ${i18n("kuunneltavissa", { language })} ${startTimeStr}`,
        label: `${i18n("kuunneltavissa", { language })} ${startTimeStr}`
      };
    case State.SRC_AUDIO:
      return {
        ariaLabel: `${i18n("kuuntele_juttu", { language })} ${title}${duration}`,
        label: i18n("kuuntele_juttu", { language })
      };
    case State.ONGOING_CHANNEL:
      return {
        ariaLabel: `${i18n("kuuntele_lahetysta", { language })} ${title}`,
        label: i18n("kuuntele_lahetysta", { language })
      };
    default:
      return {
        ariaLabel: `${i18n("kuuntele", { language })} ${title}${duration}`,
        label: i18n("kuuntele", { language })
      };
  }
};

const getPreviewButtonColors = (
  isDisabled: boolean,
  isLive: boolean,
  themeColors: ThemeColors
): {
  background: string;
  hover: string;
  icon: string;
} => {
  if (isDisabled) {
    return {
      background: themeColors.disabledBackgroundColor,
      hover: themeColors.disabledBackgroundColor,
      icon: themeColors.disabledColor
    };
  }
  if (isLive) {
    return {
      background: darkTheme.FEEDBACK_LIVE,
      hover: darkTheme.FEEDBACK_LIVE,
      icon: lightTheme.TEXT_NEGATIVE
    };
  }
  return {
    background: themeColors.fillColor,
    hover: themeColors.hoverColor,
    icon: themeColors.baseColor
  };
};

export const AudioPlayerPreviewButton = ({
  onClick,
  onKeyDown,
  state
}: {
  onClick?: () => void;
  onKeyDown?: (event: KeyboardEvent) => void;
  state: State;
}) => {
  const { useStoreState } = usePlayerStore();
  const durationInSeconds = useStoreState.durationInSeconds();
  const device = useStoreState.device();
  const isLive = useStoreState.isLive();
  const mediaData = useStoreState.media();
  const { language, theme } = useEmbedParametersContext();
  const themeColors = useThemeColors(theme);

  const isDisabled = !ONGOING_STATES.includes(state);

  const colors = useMemo(
    () => getPreviewButtonColors(isDisabled, isLive, themeColors),
    [isDisabled, isLive, themeColors]
  );

  const labels = useMemo(
    () =>
      getLabels({
        audioTitle: mediaData?.title,
        durationInSeconds,
        language,
        startTime: mediaData?.startTime,
        state
      }),
    [durationInSeconds, language, mediaData, state]
  );

  const showDuration =
    durationInSeconds !== undefined && !isDisabled && !isLive;

  if (state === State.NOT_ALLOWED || state === State.NOT_ALLOWED_CHANNEL) {
    return <NotAllowedButtonAudio />;
  }

  return (
    <AudioStrippedButton
      aria-label={labels.ariaLabel ?? labels.label}
      disabled={isDisabled}
      isMobile={device.isMobile}
      margin="0 2px 0 0"
      onClick={onClick}
      onKeyDown={onKeyDown}>
      <IconCircleWrapper
        backgroundColor={colors.background}
        hoverColor={colors.hover}>
        <PlayIcon fillColor={colors.icon} />
      </IconCircleWrapper>
      <PreviewButtonLabel color={themeColors.fillColor}>
        <span>{labels.label}</span>
        {showDuration && (
          <span>{secondsToHHMMSSFormat(durationInSeconds)}</span>
        )}
      </PreviewButtonLabel>
    </AudioStrippedButton>
  );
};
